import React from 'react';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { GET_UPCOMING_RACES_MINIMAL, GET_UPCOMING_RACES_TEASER } from '@shared/graphql/queries';
import { useAuth } from '../../contexts/AuthContext';
import { useAnalysisType } from '../../contexts/AnalysisTypeContext';
import UpcomingTips from './UpcomingTips';

const AuthenticatedUpcomingTips: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { currentAnalysisType } = useAnalysisType();
  const location = useLocation();
  
  // Use different queries based on authentication status
  const { loading, error, data } = useQuery(
    isAuthenticated ? GET_UPCOMING_RACES_MINIMAL : GET_UPCOMING_RACES_TEASER,
    {
      variables: isAuthenticated ? {
        limit: 2,
        analysisTypeId: currentAnalysisType
      } : {
        limit: 2
      },
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-and-network',
      pollInterval: 60000,
      // Always fetch - no skip condition
    }
  );
  
  // Don't render on the home page or date-specific home pages since they already have the component
  if (location.pathname === '/' || /^\/\d{8}$/.test(location.pathname)) {
    return null;
  }
  
  // Handle loading and error states
  if (loading) {
    return <div className="text-center py-2 text-gray-500 text-sm">Loading upcoming tips...</div>;
  }
  
  if (error) {
    return <div className="text-center py-2 text-bookie-red text-sm">Error loading upcoming tips</div>;
  }
  
  // Get the appropriate data based on authentication status
  const races = isAuthenticated ? data?.upcomingRacesMinimal : data?.upcomingRacesTeaser;
  
  // Don't render if no data
  if (!races || races.length === 0) {
    return null;
  }
  
  // Determine the appropriate class based on the current page
  const getContainerClass = () => {
    // Check if we're on the tips page
    if (location.pathname.match(/\/[a-z]+\/[a-z-]+\/\d+/)) {
      return 'mt-4 mb-6'; // Increased margins for tips page to match other pages
    }
    
    // Check if we're on the results page
    if (location.pathname.includes('/results')) {
      return 'mt-4 mb-6'; // Top of page on results page
    }
    
    // Default styling for other pages
    return 'my-4';
  };
  
  return (
    <div className={`${getContainerClass()} w-full`}>
      <UpcomingTips races={races} isAuthenticated={isAuthenticated} />
    </div>
  );
};

export default AuthenticatedUpcomingTips;
